/**
 * SportsStatsWidget Component
 *
 * Embeds sports data widgets for live stats including:
 * - Team standings
 * - Recent fixtures/results
 * - Top players
 * - Team statistics
 * - Opponent team info
 *
 * Supports:
 * - MLS (Houston Dynamo)
 * - NWSL (Houston Dash)
 * - NBA (Houston Rockets)
 * - Toyota Center concerts/events
 *
 * Uses external links to official team pages and ESPN for live data.
 */

import React, { useState, useMemo } from 'react';
import {
  Trophy,
  Users,
  Calendar,
  ExternalLink,
  TrendingUp,
  Swords,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getCleanEventName, getExpandedOpponentName } from '../utils/attendanceParser';

// Sports team configurations
interface TeamConfig {
  name: string;
  shortName: string;
  sport: 'soccer' | 'basketball';
  league: string;
  logo: string;
  color: string;
  bgColor: string;
  links: {
    standings: string;
    schedule: string;
    roster: string;
    espn: string;
  };
}

// Houston teams
const HOUSTON_TEAMS: Record<string, TeamConfig> = {
  dynamo: {
    name: 'Houston Dynamo FC',
    shortName: 'Dynamo',
    sport: 'soccer',
    league: 'MLS',
    logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9668.png',
    color: 'orange',
    bgColor: 'bg-orange-600/10',
    links: {
      standings: 'https://www.espn.com/soccer/standings/_/league/usa.1',
      schedule: 'https://www.espn.com/soccer/team/fixtures/_/id/9668/houston-dynamo-fc',
      roster: 'https://www.espn.com/soccer/team/squad/_/id/9668/houston-dynamo-fc',
      espn: 'https://www.espn.com/soccer/team/_/id/9668/houston-dynamo-fc',
    },
  },
  dash: {
    name: 'Houston Dash',
    shortName: 'Dash',
    sport: 'soccer',
    league: 'NWSL',
    logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9923.png',
    color: 'sky',
    bgColor: 'bg-sky-600/10',
    links: {
      standings: 'https://www.espn.com/soccer/standings/_/league/usa.nwsl',
      schedule: 'https://www.espn.com/soccer/team/fixtures/_/id/9923/houston-dash',
      roster: 'https://www.espn.com/soccer/team/squad/_/id/9923/houston-dash',
      espn: 'https://www.espn.com/soccer/team/_/id/9923/houston-dash',
    },
  },
  rockets: {
    name: 'Houston Rockets',
    shortName: 'Rockets',
    sport: 'basketball',
    league: 'NBA',
    logo: 'https://a.espncdn.com/i/teamlogos/nba/500/hou.png',
    color: 'red',
    bgColor: 'bg-red-600/10',
    links: {
      standings: 'https://www.espn.com/nba/standings',
      schedule: 'https://www.espn.com/nba/team/schedule/_/name/hou/houston-rockets',
      roster: 'https://www.espn.com/nba/team/roster/_/name/hou/houston-rockets',
      espn: 'https://www.espn.com/nba/team/_/name/hou/houston-rockets',
    },
  },
};

// NBA team lookup for opponent matching
const NBA_TEAMS: Record<string, { name: string; espnId: string; logo: string }> = {
  // Western Conference
  'lakers': { name: 'Los Angeles Lakers', espnId: 'lal', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/lal.png' },
  'warriors': { name: 'Golden State Warriors', espnId: 'gs', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/gs.png' },
  'suns': { name: 'Phoenix Suns', espnId: 'phx', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/phx.png' },
  'clippers': { name: 'Los Angeles Clippers', espnId: 'lac', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/lac.png' },
  'mavericks': { name: 'Dallas Mavericks', espnId: 'dal', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/dal.png' },
  'mavs': { name: 'Dallas Mavericks', espnId: 'dal', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/dal.png' },
  'spurs': { name: 'San Antonio Spurs', espnId: 'sa', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/sa.png' },
  'nuggets': { name: 'Denver Nuggets', espnId: 'den', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/den.png' },
  'thunder': { name: 'Oklahoma City Thunder', espnId: 'okc', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/okc.png' },
  'timberwolves': { name: 'Minnesota Timberwolves', espnId: 'min', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/min.png' },
  'wolves': { name: 'Minnesota Timberwolves', espnId: 'min', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/min.png' },
  'pelicans': { name: 'New Orleans Pelicans', espnId: 'no', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/no.png' },
  'grizzlies': { name: 'Memphis Grizzlies', espnId: 'mem', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/mem.png' },
  'jazz': { name: 'Utah Jazz', espnId: 'uta', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/uta.png' },
  'kings': { name: 'Sacramento Kings', espnId: 'sac', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/sac.png' },
  'blazers': { name: 'Portland Trail Blazers', espnId: 'por', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/por.png' },
  'trail blazers': { name: 'Portland Trail Blazers', espnId: 'por', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/por.png' },
  // Eastern Conference
  'celtics': { name: 'Boston Celtics', espnId: 'bos', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/bos.png' },
  'bucks': { name: 'Milwaukee Bucks', espnId: 'mil', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/mil.png' },
  'heat': { name: 'Miami Heat', espnId: 'mia', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/mia.png' },
  'sixers': { name: 'Philadelphia 76ers', espnId: 'phi', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/phi.png' },
  '76ers': { name: 'Philadelphia 76ers', espnId: 'phi', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/phi.png' },
  'knicks': { name: 'New York Knicks', espnId: 'ny', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/ny.png' },
  'nets': { name: 'Brooklyn Nets', espnId: 'bkn', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/bkn.png' },
  'cavaliers': { name: 'Cleveland Cavaliers', espnId: 'cle', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/cle.png' },
  'cavs': { name: 'Cleveland Cavaliers', espnId: 'cle', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/cle.png' },
  'bulls': { name: 'Chicago Bulls', espnId: 'chi', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/chi.png' },
  'hawks': { name: 'Atlanta Hawks', espnId: 'atl', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/atl.png' },
  'raptors': { name: 'Toronto Raptors', espnId: 'tor', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/tor.png' },
  'hornets': { name: 'Charlotte Hornets', espnId: 'cha', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/cha.png' },
  'wizards': { name: 'Washington Wizards', espnId: 'wsh', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/wsh.png' },
  'magic': { name: 'Orlando Magic', espnId: 'orl', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/orl.png' },
  'pistons': { name: 'Detroit Pistons', espnId: 'det', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/det.png' },
  'pacers': { name: 'Indiana Pacers', espnId: 'ind', logo: 'https://a.espncdn.com/i/teamlogos/nba/500/ind.png' },
};

// MLS team lookup for opponent matching
const MLS_TEAMS: Record<string, { name: string; espnId: string; logo: string }> = {
  'fc dallas': { name: 'FC Dallas', espnId: '9664', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9664.png' },
  'dallas': { name: 'FC Dallas', espnId: '9664', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9664.png' },
  'austin fc': { name: 'Austin FC', espnId: '22652', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/22652.png' },
  'austin': { name: 'Austin FC', espnId: '22652', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/22652.png' },
  'la galaxy': { name: 'LA Galaxy', espnId: '9666', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9666.png' },
  'galaxy': { name: 'LA Galaxy', espnId: '9666', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9666.png' },
  'lafc': { name: 'LAFC', espnId: '21241', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/21241.png' },
  'los angeles fc': { name: 'LAFC', espnId: '21241', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/21241.png' },
  'sporting kc': { name: 'Sporting Kansas City', espnId: '9669', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9669.png' },
  'sporting kansas city': { name: 'Sporting Kansas City', espnId: '9669', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9669.png' },
  'minnesota united': { name: 'Minnesota United', espnId: '17362', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/17362.png' },
  'colorado rapids': { name: 'Colorado Rapids', espnId: '9663', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9663.png' },
  'rapids': { name: 'Colorado Rapids', espnId: '9663', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9663.png' },
  'real salt lake': { name: 'Real Salt Lake', espnId: '9673', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9673.png' },
  'rsl': { name: 'Real Salt Lake', espnId: '9673', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9673.png' },
  'seattle sounders': { name: 'Seattle Sounders', espnId: '9675', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9675.png' },
  'sounders': { name: 'Seattle Sounders', espnId: '9675', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9675.png' },
  'portland timbers': { name: 'Portland Timbers', espnId: '9671', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9671.png' },
  'timbers': { name: 'Portland Timbers', espnId: '9671', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9671.png' },
  'vancouver whitecaps': { name: 'Vancouver Whitecaps', espnId: '9677', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9677.png' },
  'whitecaps': { name: 'Vancouver Whitecaps', espnId: '9677', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9677.png' },
  'san jose earthquakes': { name: 'San Jose Earthquakes', espnId: '9674', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9674.png' },
  'earthquakes': { name: 'San Jose Earthquakes', espnId: '9674', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9674.png' },
  'inter miami': { name: 'Inter Miami CF', espnId: '22016', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/22016.png' },
  'miami': { name: 'Inter Miami CF', espnId: '22016', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/22016.png' },
  'atlanta united': { name: 'Atlanta United', espnId: '18509', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/18509.png' },
  'atlanta': { name: 'Atlanta United', espnId: '18509', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/18509.png' },
  'new york red bulls': { name: 'New York Red Bulls', espnId: '9670', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9670.png' },
  'red bulls': { name: 'New York Red Bulls', espnId: '9670', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/9670.png' },
  'nycfc': { name: 'New York City FC', espnId: '15296', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/15296.png' },
  'new york city fc': { name: 'New York City FC', espnId: '15296', logo: 'https://a.espncdn.com/i/teamlogos/soccer/500/15296.png' },
};

type WidgetTab = 'home' | 'opponent' | 'matchup';

interface SportsStatsWidgetProps {
  eventType: string;
  opponent?: string | null;
  className?: string;
}

/**
 * Tab button component
 */
const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150
      min-h-[44px] touch-manipulation
      ${active
        ? 'bg-brand-500 text-white'
        : 'bg-dark-border/50 text-dark-muted hover:text-white hover:bg-dark-border'
      }
    `}
  >
    {icon}
    <span>{label}</span>
  </button>
);

/**
 * Team Card Component - Shows team info with links
 */
const TeamCard: React.FC<{
  team: TeamConfig;
  isOpponent?: boolean;
  opponentName?: string;
  opponentLogo?: string;
  opponentEspnUrl?: string;
}> = ({ team, isOpponent, opponentName, opponentLogo, opponentEspnUrl }) => {
  const [isExpanded, setIsExpanded] = useState(!isOpponent);

  const displayName = isOpponent && opponentName ? opponentName : team.name;
  const displayLogo = isOpponent && opponentLogo ? opponentLogo : team.logo;
  const espnUrl = isOpponent && opponentEspnUrl ? opponentEspnUrl : team.links.espn;

  return (
    <div className={`team-card rounded-xl border border-dark-border/30 overflow-hidden ${team.bgColor}`}>
      {/* Team Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-dark-border/20 transition-all duration-150"
      >
        <div className="flex items-center gap-3">
          <div className="sports-team-logo w-12 h-12 bg-dark-surface rounded-full p-1 flex items-center justify-center shadow-sm">
            <img
              src={displayLogo}
              alt={displayName}
              className="w-10 h-10 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://a.espncdn.com/i/teamlogos/default-team-logo-500.png';
              }}
            />
          </div>
          <div className="text-left">
            <h4 className="text-white font-bold">{displayName}</h4>
            <p className="text-xs text-dark-muted">
              {isOpponent ? 'Opponent' : team.league}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-dark-muted" />
        ) : (
          <ChevronDown size={20} className="text-dark-muted" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-2">
            <a
              href={isOpponent ? espnUrl : team.links.standings}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 bg-dark-border/30 rounded-lg hover:bg-dark-border/50 transition-all duration-150"
            >
              <Trophy size={16} className="text-yellow-500" />
              <span className="text-sm text-white">Standings</span>
              <ExternalLink size={12} className="text-dark-muted ml-auto" />
            </a>
            <a
              href={isOpponent ? espnUrl : team.links.schedule}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 bg-dark-border/30 rounded-lg hover:bg-dark-border/50 transition-all duration-150"
            >
              <Calendar size={16} className="text-blue-500" />
              <span className="text-sm text-white">Schedule</span>
              <ExternalLink size={12} className="text-dark-muted ml-auto" />
            </a>
            <a
              href={isOpponent ? espnUrl : team.links.roster}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 bg-dark-border/30 rounded-lg hover:bg-dark-border/50 transition-all duration-150"
            >
              <Users size={16} className="text-green-500" />
              <span className="text-sm text-white">Roster</span>
              <ExternalLink size={12} className="text-dark-muted ml-auto" />
            </a>
            <a
              href={espnUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 bg-dark-border/30 rounded-lg hover:bg-dark-border/50 transition-all duration-150"
            >
              <TrendingUp size={16} className="text-red-500" />
              <span className="text-sm text-white">Stats</span>
              <ExternalLink size={12} className="text-dark-muted ml-auto" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Match opponent name to team data
 *
 * First cleans the event name (removes EB/PC suffixes, times, etc.)
 * Then expands abbreviated team names to full official names
 * Finally matches against the team lookup tables
 */
const findOpponentTeam = (
  opponentName: string | null | undefined,
  sport: 'soccer' | 'basketball'
): { name: string; logo: string; espnUrl: string } | null => {
  if (!opponentName) return null;

  // First, get the expanded opponent name (e.g., "New Orleans" -> "New Orleans Pelicans")
  const expandedName = getExpandedOpponentName(opponentName);

  // Use expanded name if available, otherwise clean the raw name
  const searchName = (expandedName || getCleanEventName(opponentName) || opponentName).toLowerCase().trim();

  if (sport === 'basketball') {
    // Search NBA teams using the expanded/cleaned name
    for (const [key, team] of Object.entries(NBA_TEAMS)) {
      // Check if the search name contains the key OR if it matches the full team name
      if (searchName.includes(key) ||
          team.name.toLowerCase().includes(searchName) ||
          searchName.includes(team.name.toLowerCase())) {
        return {
          name: team.name,
          logo: team.logo,
          espnUrl: `https://www.espn.com/nba/team/_/name/${team.espnId}`,
        };
      }
    }
  } else {
    // Search MLS teams
    for (const [key, team] of Object.entries(MLS_TEAMS)) {
      if (searchName.includes(key) ||
          team.name.toLowerCase().includes(searchName) ||
          searchName.includes(team.name.toLowerCase())) {
        return {
          name: team.name,
          logo: team.logo,
          espnUrl: `https://www.espn.com/soccer/team/_/id/${team.espnId}`,
        };
      }
    }
  }

  return null;
};

/**
 * SportsStatsWidget Component
 */
export const SportsStatsWidget: React.FC<SportsStatsWidgetProps> = ({
  eventType,
  opponent,
  className = '',
}) => {
  const [activeTab, setActiveTab] = useState<WidgetTab>('home');

  // Determine which Houston team config to use
  // Check for "rockets" in eventType or if venue is Toyota Center (NBA games)
  const teamKey = eventType.toLowerCase();
  const isRocketsGame = teamKey === 'rockets' ||
    (opponent?.toLowerCase().includes('rockets')) ||
    // If opponent contains an NBA team name, treat as Rockets game
    Object.keys(NBA_TEAMS).some(key => opponent?.toLowerCase().includes(key));

  const teamConfig = isRocketsGame
    ? HOUSTON_TEAMS.rockets
    : HOUSTON_TEAMS[teamKey];

  // Find opponent team info
  const opponentTeam = useMemo(() => {
    if (!teamConfig) return null;
    return findOpponentTeam(opponent, teamConfig.sport);
  }, [opponent, teamConfig?.sport]);

  // If not a supported sports team, don't render
  if (!teamConfig) {
    return null;
  }

  return (
    <div className={`sports-stats-widget bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className={`sports-stats-widget-header px-4 py-3 border-b border-dark-border/30 ${teamConfig.bgColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Swords size={20} className="text-white" />
            <div>
              <h3 className="text-sm font-bold text-white">Team Stats</h3>
              <p className="text-xs text-dark-muted">{teamConfig.league} • Live Data</p>
            </div>
          </div>
          {/* ESPN Logo Button */}
          <a
            href={teamConfig.links.espn}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D00] hover:bg-[#B00] transition-all duration-150 rounded-md min-h-[36px]"
            title="View on ESPN"
          >
            {/* ESPN Logo - Use ESPN CDN image for authenticity */}
            <img
              src="https://a.espncdn.com/redesign/assets/img/logos/logo-espn-82x20.png"
              alt="ESPN"
              className="h-4 w-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </a>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation flex gap-2 px-4 py-3 border-b border-dark-border/30 bg-dark-border/20 overflow-x-auto">
        <TabButton
          active={activeTab === 'home'}
          onClick={() => setActiveTab('home')}
          icon={<Trophy size={16} />}
          label="Teams"
        />
        <TabButton
          active={activeTab === 'matchup'}
          onClick={() => setActiveTab('matchup')}
          icon={<Swords size={16} />}
          label="Match-Up"
        />
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'home' && (
          <div className="space-y-4">
            {/* Home Team */}
            <TeamCard team={teamConfig} />

            {/* Opponent Team - Always show if available */}
            {opponentTeam && (
              <TeamCard
                team={teamConfig}
                isOpponent
                opponentName={opponentTeam.name}
                opponentLogo={opponentTeam.logo}
                opponentEspnUrl={opponentTeam.espnUrl}
              />
            )}

            {/* Show generic opponent if no match found but opponent name exists */}
            {!opponentTeam && opponent && (
              <div className="team-card rounded-xl border border-dark-border/30 overflow-hidden bg-dark-border/20">
                <div className="flex items-center gap-3 p-4">
                  <div className="sports-team-logo w-12 h-12 bg-dark-surface rounded-full p-1 flex items-center justify-center shadow-sm">
                    <img
                      src="https://a.espncdn.com/i/teamlogos/default-team-logo-500.png"
                      alt={opponent}
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                  <div className="text-left">
                    <h4 className="text-white font-bold">{opponent}</h4>
                    <p className="text-xs text-dark-muted">Opponent</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'matchup' && (
          <div className="space-y-4">
            {/* Matchup Header */}
            <div className="flex items-center justify-center gap-4 py-4">
              <div className="text-center">
                <div className="sports-matchup-logo w-16 h-16 bg-dark-surface rounded-full p-2 mx-auto mb-2 shadow-sm">
                  <img
                    src={teamConfig.logo}
                    alt={teamConfig.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-sm font-medium text-white">{teamConfig.shortName}</p>
              </div>

              <div className="text-2xl font-bold text-dark-muted">VS</div>

              <div className="text-center">
                <div className="sports-matchup-logo w-16 h-16 bg-dark-surface rounded-full p-2 mx-auto mb-2 shadow-sm">
                  <img
                    src={opponentTeam?.logo || 'https://a.espncdn.com/i/teamlogos/default-team-logo-500.png'}
                    alt={opponentTeam?.name || 'Opponent'}
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-sm font-medium text-white">
                  {opponentTeam?.name || opponent || 'TBD'}
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-1 gap-2">
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(`${teamConfig.name} vs ${opponentTeam?.name || opponent} head to head`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-dark-border/30 rounded-lg hover:bg-dark-border/50 transition-all duration-150"
              >
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-purple-500" />
                  <span className="text-sm text-white">Head-to-Head History</span>
                </div>
                <ExternalLink size={14} className="text-dark-muted" />
              </a>
              <a
                href={`https://www.espn.com/${teamConfig.sport === 'basketball' ? 'nba' : 'soccer'}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-dark-border/30 rounded-lg hover:bg-dark-border/50 transition-all duration-150"
              >
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-blue-500" />
                  <span className="text-sm text-white">Live Scores & Updates</span>
                </div>
                <ExternalLink size={14} className="text-dark-muted" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SportsStatsWidget;
